import { FC, useState, useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { BindApiKeyRequest } from '@/types';
import { systemApi, ApiError } from '@/services/api';

interface SystemOption {
  id: string;
  name: string;
  interface_url: string;
  description?: string;
}

interface ApiKeyBindFormProps {
  onSubmit: (data: BindApiKeyRequest) => Promise<void>;
  loading: boolean;
}

export const ApiKeyBindForm: FC<ApiKeyBindFormProps> = ({ onSubmit, loading }) => {
  const [formData, setFormData] = useState<BindApiKeyRequest>({
    api_key: '',
    key_name: '',
    system_id: '',
    system_name: '',
    notes: ''
  });

  const [formErrors, setFormErrors] = useState<Partial<Record<keyof BindApiKeyRequest, string>>>({});

  // 系统选项状态管理
  const [systemOptions, setSystemOptions] = useState<SystemOption[]>([]);
  const [systemsLoading, setSystemsLoading] = useState(true);
  const [systemsError, setSystemsError] = useState('');

  // 加载系统列表
  useEffect(() => {
    loadSystems();
  }, []);

  const loadSystems = async () => {
    try {
      setSystemsLoading(true);
      setSystemsError('');
      const systemList = await systemApi.getSystemsForSelection();
      console.log('ApiKeyBindForm - 获取到的系统列表:', systemList);
      setSystemOptions(systemList);
    } catch (err) {
      console.error('Failed to load systems:', err);
      setSystemsError(err instanceof ApiError ? err.message : '加载系统列表失败');
    } finally {
      setSystemsLoading(false);
    }
  };

  const validateForm = () => {
    const errors: Partial<Record<keyof BindApiKeyRequest, string>> = {};

    if (!formData.api_key.trim()) {
      errors.api_key = 'API Key不能为空';
    } else if (!formData.api_key.startsWith('sk-') && !formData.api_key.startsWith('cr_') && !formData.api_key.startsWith('bm_')) {
      errors.api_key = 'API Key格式不正确，应以sk-、cr_或bm_开头';
    }

    if (!formData.key_name.trim()) {
      errors.key_name = 'Key名称不能为空';
    } else if (formData.key_name.length < 2) {
      errors.key_name = 'Key名称至少2个字符';
    }

    if (!formData.system_id.trim()) {
      errors.system_id = '请选择系统';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm() || loading) {
      return;
    }

    // 根据选择的系统ID设置系统名称
    const selectedSystem = systemOptions.find(sys => sys.id === formData.system_id);
    const submitData = {
      ...formData,
      system_name: selectedSystem?.name || formData.system_id
    };

    try {
      await onSubmit(submitData);
      // 重置表单
      setFormData({
        api_key: '',
        key_name: '',
        system_id: '',
        system_name: '',
        notes: ''
      });
      setFormErrors({});
    } catch (error) {
      // 错误处理由父组件处理
    }
  };

  const handleInputChange = (field: keyof BindApiKeyRequest, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // 清除该字段的错误
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-gray-50 rounded-lg p-6 border border-gray-200">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* API Key输入 */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            API Key *
          </label>
          <input
            type="text"
            value={formData.api_key}
            onChange={(e) => handleInputChange('api_key', e.target.value)}
            placeholder="请输入API Key (例如: sk-xxx..., cr_xxx... 或 bm_xxx...)"
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm ${
              formErrors.api_key ? 'border-red-300' : 'border-gray-300'
            }`}
            disabled={loading}
          />
          {formErrors.api_key && (
            <p className="mt-1 text-sm text-red-600">{formErrors.api_key}</p>
          )}
        </div>

        {/* Key名称 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Key名称 *
          </label>
          <input
            type="text"
            value={formData.key_name}
            onChange={(e) => handleInputChange('key_name', e.target.value)}
            placeholder="为这个API Key起个名字"
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
              formErrors.key_name ? 'border-red-300' : 'border-gray-300'
            }`}
            disabled={loading}
            maxLength={50}
          />
          {formErrors.key_name && (
            <p className="mt-1 text-sm text-red-600">{formErrors.key_name}</p>
          )}
        </div>

        {/* 系统选择 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            所属系统 *
          </label>
          {systemsError ? (
            <div className="text-sm text-red-600">
              <span className="mr-2">⚠️</span>
              {systemsError}
              <button
                type="button"
                onClick={loadSystems}
                className="ml-2 text-primary-600 hover:text-primary-800 underline"
              >
                重试
              </button>
            </div>
          ) : (
            <select
              value={formData.system_id}
              onChange={(e) => handleInputChange('system_id', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                formErrors.system_id ? 'border-red-300' : 'border-gray-300'
              }`}
              disabled={loading || systemsLoading}
            >
              <option value="">
                {systemsLoading ? '加载系统中...' : '请选择系统节点'}
              </option>
              {systemOptions.map((system) => (
                <option key={system.id} value={system.id}>
                  {system.name}
                </option>
              ))}
            </select>
          )}
          {formErrors.system_id && (
            <p className="mt-1 text-sm text-red-600">{formErrors.system_id}</p>
          )}
        </div>

        {/* 备注 */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            备注 (可选)
          </label>
          <textarea
            value={formData.notes}
            onChange={(e) => handleInputChange('notes', e.target.value)}
            placeholder="添加一些备注信息..."
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
            disabled={loading}
            maxLength={200}
          />
          <div className="text-xs text-gray-400 mt-1 text-right">
            {formData.notes?.length || 0}/200
          </div>
        </div>
      </div>

      <div className="flex justify-end mt-6">
        <Button
          type="submit"
          disabled={loading}
          className="bg-green-600 hover:bg-green-700 disabled:opacity-50"
        >
          {loading ? (
            <>
              <span className="animate-spin mr-2">⏳</span>
              绑定中...
            </>
          ) : (
            <>
              🔗 绑定API Key
            </>
          )}
        </Button>
      </div>
    </form>
  );
};